import React, { useState } from "react";
import {
  Box,
  Card,
  Tabs,
  Tab,
  TextField,
  Button,
  Typography,
  IconButton,
  InputAdornment,
  Link,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";

function TabPanel({ children, value, index }) {
  return (
    <Box
      role="tabpanel"
      hidden={value !== index}
      sx={{
        width: "100%",
        flexGrow: 1,
        minHeight: 400,
        display: value === index ? "block" : "none",
        transition: "opacity 0.2s ease",
      }}
    >
      {value === index && <Box sx={{ mt: 2 }}>{children}</Box>}
    </Box>
  );
}

export default function AuthForm({ onLogin, onSignup, defaultMode }) {
  const [tab, setTab] = useState(
    defaultMode === "signup" ? 1 : defaultMode === "login" ? 0 : 0,
  );

  const [loginForm, setLoginForm] = useState({ email: "", password: "" });
  const [loginErrors, setLoginErrors] = useState({});
  const [loginGeneralError, setLoginGeneralError] = useState("");

  const [signupForm, setSignupForm] = useState({
    firstName: "",
    lastName: "",
    email: "",
    password: "",
    confirmPassword: "",
  });
  const [signupErrors, setSignupErrors] = useState({});
  const [signupGeneralError, setSignupGeneralError] = useState("");

  // Password visibility
  const [showLoginPassword, setShowLoginPassword] = useState(false);
  const [showSignupPassword, setShowSignupPassword] = useState(false);
  const [showSignupConfirmPassword, setShowSignupConfirmPassword] =
    useState(false);

  const handleTabChange = (_, newValue) => setTab(newValue);

  // --- Validation functions ---
  const validateLogin = () => {
    const errs = {};
    if (!loginForm.email) errs.email = "Email is required";
    else if (!/^[^\s@]+@[^\s@]+$/.test(loginForm.email))
      errs.email = "Invalid email";
    if (!loginForm.password) errs.password = "Password is required";
    return errs;
  };

  const validateSignup = () => {
    const errs = {};
    if (!signupForm.firstName) errs.firstName = "First Name is required";
    if (!signupForm.lastName) errs.lastName = "Last Name is required";
    if (!signupForm.email) errs.email = "Email is required";
    else if (!/\S+@\S+\.\S+/.test(signupForm.email))
      errs.email = "Invalid email";
    if (!signupForm.password) errs.password = "Password is required";
    if (signupForm.password !== signupForm.confirmPassword)
      errs.confirmPassword = "Passwords do not match";
    return errs;
  };

  // --- Change handlers ---
  const handleLoginChange = (e) => {
    setLoginForm({ ...loginForm, [e.target.name]: e.target.value });
    setLoginErrors({ ...loginErrors, [e.target.name]: null });
  };

  const handleSignupChange = (e) => {
    setSignupForm({ ...signupForm, [e.target.name]: e.target.value });
    setSignupErrors({ ...signupErrors, [e.target.name]: null });
  };

  // --- Submit handlers ---
  const handleLoginSubmit = async (e) => {
    e.preventDefault();
    const errs = validateLogin();
    if (Object.keys(errs).length > 0) return setLoginErrors(errs);
    setLoginGeneralError("");

    try {
      const result = await onLogin(loginForm);
      if (!result.success) {
        setLoginGeneralError(
          result.message || "Invalid credentials. Please try again.",
        );
      }
    } catch {
      setLoginGeneralError("Something went wrong. Please try again.");
    }
  };

  const handleSignupSubmit = async (e) => {
    e.preventDefault();
    const errs = validateSignup();
    if (Object.keys(errs).length > 0) return setSignupErrors(errs);
    setSignupGeneralError("");

    try {
      const result = await onSignup(signupForm);
      if (!result.success) {
        if (result.errors) {
          setSignupErrors(
            Object.fromEntries(result.errors.map((er) => [er.path, er.msg])),
          );
        }
        setSignupGeneralError(
          result.message || "Registration failed. Please check your inputs.",
        );
      }
    } catch {
      setSignupGeneralError("Something went wrong. Please try again.");
    }
  };

  return (
    <Card
      sx={{
        width: "100%",
        maxWidth: 500,
        p: 4,
        borderRadius: 3,
        boxShadow: 4,
        display: "flex",
        flexDirection: "column",
        flexGrow: 1,
      }}
    >
      <Tabs
        value={tab}
        onChange={handleTabChange}
        variant="fullWidth"
        textColor="primary"
        indicatorColor="primary"
      >
        <Tab label="Login" />
        <Tab label="Sign Up" />
      </Tabs>

      {/* Login Form */}
      <TabPanel value={tab} index={0}>
        <Box component="form" onSubmit={handleLoginSubmit} noValidate>
          <TextField
            label="Email"
            name="email"
            type="email"
            fullWidth
            margin="normal"
            value={loginForm.email}
            onChange={handleLoginChange}
            error={!!loginErrors.email}
            helperText={loginErrors.email}
          />
          <TextField
            label="Password"
            name="password"
            type={showLoginPassword ? "text" : "password"}
            fullWidth
            margin="normal"
            value={loginForm.password}
            onChange={handleLoginChange}
            error={!!loginErrors.password}
            helperText={loginErrors.password}
            slotProps={{
              input: {
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowLoginPassword((prev) => !prev)}
                    >
                      {showLoginPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              },
            }}
          />
          {loginGeneralError && (
            <Typography color="error" variant="body2" sx={{ mt: 1 }}>
              {loginGeneralError}
            </Typography>
          )}
          <Button
            type="submit"
            variant="contained"
            color="primary"
            fullWidth
            sx={{ mt: 2 }}
          >
            Login
          </Button>
          <Typography
            variant="body2"
            align="center"
            sx={{ mt: 2, color: "text.secondary" }}
          >
            Don't have an account?{" "}
            <Link
              component="button"
              type="button"
              variant="body2"
              onClick={() => setTab(1)}
              sx={{ fontWeight: 600, cursor: "pointer" }}
            >
              Sign up
            </Link>
          </Typography>
        </Box>
      </TabPanel>

      {/* Sign Up Form */}
      <TabPanel value={tab} index={1}>
        <Box component="form" onSubmit={handleSignupSubmit} noValidate>
          <TextField
            label="First Name"
            name="firstName"
            fullWidth
            margin="normal"
            value={signupForm.firstName}
            onChange={handleSignupChange}
            error={!!signupErrors.firstName}
            helperText={signupErrors.firstName}
          />
          <TextField
            label="Last Name"
            name="lastName"
            fullWidth
            margin="normal"
            value={signupForm.lastName}
            onChange={handleSignupChange}
            error={!!signupErrors.lastName}
            helperText={signupErrors.lastName}
          />
          <TextField
            label="Email"
            name="email"
            type="email"
            fullWidth
            margin="normal"
            value={signupForm.email}
            onChange={handleSignupChange}
            error={!!signupErrors.email}
            helperText={signupErrors.email}
          />
          <TextField
            label="Password"
            name="password"
            type={showSignupPassword ? "text" : "password"}
            fullWidth
            margin="normal"
            value={signupForm.password}
            onChange={handleSignupChange}
            error={!!signupErrors.password}
            helperText={signupErrors.password}
            slotProps={{
              input: {
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowSignupPassword((prev) => !prev)}
                    >
                      {showSignupPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              },
            }}
          />
          <TextField
            label="Confirm Password"
            name="confirmPassword"
            type={showSignupConfirmPassword ? "text" : "password"}
            fullWidth
            margin="normal"
            value={signupForm.confirmPassword}
            onChange={handleSignupChange}
            error={!!signupErrors.confirmPassword}
            helperText={signupErrors.confirmPassword}
            slotProps={{
              input: {
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() =>
                        setShowSignupConfirmPassword((prev) => !prev)
                      }
                    >
                      {showSignupConfirmPassword ? (
                        <VisibilityOff />
                      ) : (
                        <Visibility />
                      )}
                    </IconButton>
                  </InputAdornment>
                ),
              },
            }}
          />
          {signupGeneralError && (
            <Typography color="error" variant="body2" sx={{ mt: 1 }}>
              {signupGeneralError}
            </Typography>
          )}
          <Button
            type="submit"
            variant="contained"
            color="primary"
            fullWidth
            sx={{ mt: 2 }}
          >
            Sign Up
          </Button>
          <Typography
            variant="body2"
            align="center"
            sx={{ mt: 2, color: "text.secondary" }}
          >
            Already have an account?{" "}
            <Link
              component="button"
              type="button"
              variant="body2"
              onClick={() => setTab(0)}
              sx={{ fontWeight: 600, cursor: "pointer" }}
            >
              Login
            </Link>
          </Typography>
        </Box>
      </TabPanel>
    </Card>
  );
}
